import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { API_BASE_URL, ADMIN_API_BASE_URL, XMEDICAL_CONFIG } from '../../config';
import { LocalStorageService } from '../../storage';
import { Practice } from '../../repositories';

/**
 * HTTP Interceptor that adds the practice ID header to API requests.
 *
 * This interceptor supports multi-tenant architecture by adding the selected
 * practice context to each request.
 *
 * Headers added:
 * - `x-practice-id`
 *
 * @example
 * { provide: HTTP_INTERCEPTORS, useClass: PracticeInterceptor, multi: true }
 */
@Injectable({
  providedIn: 'root'
})
export class PracticeInterceptor implements HttpInterceptor {
  private baseUrl = inject(API_BASE_URL);
  private adminUrl = inject(ADMIN_API_BASE_URL);
  private config = inject(XMEDICAL_CONFIG);
  private localStorage = inject(LocalStorageService);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.url.indexOf(this.adminUrl) > -1 || request.url.indexOf(this.baseUrl) > -1) {
      return this.addPracticeHeader(request, next);
    }
    return next.handle(request);
  }

  private addPracticeHeader(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.config.storageKeys) {
      throw new Error('XMedicalConfig.storageKeys is required but not provided');
    }

    const practiceKey = this.config.storageKeys.selectedPractice;
    const selectedPractice = this.localStorage.getItem<Practice>(practiceKey) as Practice;

    if (selectedPractice && selectedPractice.id) {
      // Preserve existing headers
      const headerSettings: { [name: string]: string | string[]; } = {};
      for (const key of request.headers.keys()) {
        headerSettings[key] = request.headers.getAll(key);
      }

      headerSettings['x-practice-id'] = selectedPractice.id.toString();

      const newHeader = new HttpHeaders(headerSettings);
      const changedRequest = request.clone({ headers: newHeader });
      return next.handle(changedRequest);
    }

    return next.handle(request);
  }
}
